import { fetchAndTrackOfflineStatus, isFailedToFetchError } from "../../common/utils/fetchAndTrackOfflineStatus";
import { uuid } from "../../common/utils/uuid";
import { withRetry } from "../../common/utils/withRetry";
import { browserId, sessionId } from "../../js/core/browserAndSession";
import getLogger, { LogGroup } from "../../js/core/logger";
import { auth } from "../../js/firebase/auth";
import { isCachedHandler, serializeRequest } from "../_utils/cachedHandler";
import type { BinaryHandlerResponse } from "./types/server";
import {
    BadGatewayError,
    BadRequestError,
    BaseApiError,
    ConflictError,
    ContentTooLargeError,
    ErrorResponseBody,
    ForbiddenError,
    IdempotentRequestAlreadyHandledError,
    InternalServerError,
    MaintenanceModeError,
    NotFoundError,
    PaymentRequiredError,
    ServiceUnavailableError,
    TooManyRequestsError,
    UnauthorizedError,
    UnprocessableContentError
} from "./types/errors";

const logger = getLogger(LogGroup.API);

declare global {
    interface Window {
        overrideIdToken: string;
        apis: Record<string, string>;
    }
}

export interface CallOptions {
    /**
     * Bypasses the browser cache, only applies to get...Cached handlers
     */
    reloadCache?: boolean;
}

async function callHandler<Response, Request>(apiName: string, handlerName: string, request: Request, options: CallOptions = {}, useLogger: boolean = true): Promise<Response> {
    let idToken: string | undefined = undefined;
    if (window.overrideIdToken) {
        idToken = window.overrideIdToken;
    } else {
        const currentUser = auth().currentUser;
        if (currentUser) {
            idToken = await currentUser.getIdToken();
        }
    }

    const baseUrl = window.apis[`api-${apiName.toLowerCase()}`];

    // eslint-disable-next-line no-undef
    const headers: HeadersInit = {
        "Content-Type": "application/json",
        "X-Client-Browser-Id": browserId,
        "X-Client-Session-Id": sessionId,
        "X-Idempotency-Key": uuid()
    };

    if (idToken) {
        headers["Authorization"] = `Bearer ${idToken}`;
    }

    // Workspace integrations can submit provider credentials. Never place them in client logs.
    const loggedRequest = request && typeof request === "object" && "apiKey" in request
        ? { ...request, apiKey: "[REDACTED]" }
        : request;
    let url = `/${handlerName}`;
    let method = "POST";
    let body = JSON.stringify(request);
    // eslint-disable-next-line no-undef
    let cache: RequestCache = undefined;

    if (isCachedHandler(handlerName)) {
        url += `?${serializeRequest(request)}`;
        method = "GET";
        body = undefined;
        if (options.reloadCache) {
            cache = "reload";
        }
    }

    const call = async () => {
        const res = await fetchAndTrackOfflineStatus(`${baseUrl}${url}`, {
            method,
            body,
            credentials: "same-origin",
            headers,
            ...(cache ? { cache } : {})
        });

        if (res.status < 400) {
            return res;
        }

        const responseBody: ErrorResponseBody = (await res.json().catch(() => { })) ?? {};
        responseBody.requestId = res.headers.get("X-Request-Id");

        if (res.status === 400) {
            throw new BadRequestError(responseBody.message, responseBody);
        }
        if (res.status === 404) {
            throw new NotFoundError(responseBody.message, responseBody);
        }
        if (res.status === 401) {
            throw new UnauthorizedError(responseBody.message, responseBody);
        }
        if (res.status === 403) {
            throw new ForbiddenError(responseBody.message, responseBody);
        }
        if (res.status === 429) {
            throw new TooManyRequestsError(responseBody.message, responseBody);
        }
        if (res.status === 409) {
            throw new ConflictError(responseBody.message, responseBody);
        }
        if (res.status === 402) {
            throw new PaymentRequiredError(responseBody.message, responseBody);
        }
        if (res.status === 413) {
            throw new ContentTooLargeError(responseBody.message, responseBody);
        }
        if (res.status === 422) {
            throw new UnprocessableContentError(responseBody.message, responseBody);
        }
        if (res.status === 423) {
            throw new MaintenanceModeError(responseBody.message, responseBody);
        }
        if (res.status === 428) {
            throw new IdempotentRequestAlreadyHandledError(responseBody.message, responseBody);
        }
        if (res.status === 502) {
            throw new BadGatewayError(responseBody.message, responseBody);
        }
        if (res.status === 503) {
            throw new ServiceUnavailableError(responseBody.message, responseBody);
        }

        throw new InternalServerError(responseBody.message, responseBody);
    };

    try {
        const res = await withRetry(
            () => call(),
            {
                shouldBackoff: true,
                maxAttempts: 5,
                checkRetryCondition: (error, response) => {
                    return (error as BaseApiError)?.status === 502 ||
                        (error as BaseApiError)?.status === 503 ||
                        (error as BaseApiError)?.status === 429 ||
                        isFailedToFetchError(error);
                },
                onBeforeRetry: async (error, response, retriesRemaining: number) => {
                    const logData = {
                        url,
                        method,
                        status: (error as BaseApiError)?.status,
                        body: loggedRequest,
                        endpointName: handlerName,
                        apiName,
                        requestId: (error as BaseApiError)?.responseBody?.requestId,
                        retriesRemaining
                    };

                    if (useLogger) {
                        logger.warn(`[callable][${apiName}] request failed, retrying`, logData);
                    } else {
                        // eslint-disable-next-line no-console
                        console.warn(`[callable][${apiName}] request failed, retrying`, logData);
                    }
                }
            }
        );
        const contentType = res.headers.get("Content-Type")?.toLowerCase();
        if (contentType && !contentType.includes("application/json")) {
            return res.blob() as Promise<Response>;
        }
        return res.json() as Promise<Response>;
    } catch (err) {
        if ((err as BaseApiError)?.status === 423 && idToken) {
            // We do it here because we don't want to throw an error, and log the request details
            //  we want to redirect to the maintenance page instead
            window.location.href = "/maintenance";
            return;
        }

        const logData = {
            url,
            method,
            status: (err as BaseApiError)?.status,
            body: loggedRequest,
            requestId: (err as BaseApiError)?.responseBody?.requestId,
            endpointName: handlerName,
            apiName
        };

        if (useLogger) {
            logger.error(err, `[callable][${apiName}] request failed`, logData);
        } else {
            // eslint-disable-next-line no-console
            console.error(err, `[callable][${apiName}] request failed`, logData);
        }

        throw err;
    }
}

type ClientResponse<Response> = Response extends BinaryHandlerResponse ? Blob : Response;

type OmitAuth<T> = T extends (request: infer R, auth: any) => Promise<infer Response>
    ? (request: R, options?: CallOptions) => Promise<ClientResponse<Response>>
    : never;

type Callables<Handlers> = {
    [K in keyof Handlers]: OmitAuth<Handlers[K]>
};

export function createCallable<Handlers>(apiName: string, useLogger: boolean = true) {
    return new Proxy({}, {
        get(target, handlerName: string) {
            return (request: any, options?: CallOptions) => callHandler(apiName, handlerName, request, options, useLogger);
        }
    }) as unknown as Callables<Handlers>;
}
